/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */
/* eslint-disable @typescript-eslint/naming-convention */

import { ListLike, PLAN_CONSTANTS, TicketFields, guessXFromDate } from '@touchplan/common';
import { CalendarWorkdays, IMetadataForDateRangeResponse } from '../models/CalendarWorkdays';
import { GroupedTicket } from '../models/GroupedTicket';
import { Side } from '@touchplan/common';
import { Ticket } from '@touchplan/common';
import { plannedOnly } from '@touchplan/common';
import { applyVarianceToPin, clonePin } from '@touchplan/common';
import { logger } from '@touchplan/logging';

interface IDurationUpdateData {
	liveStart?: string;
	liveExceptionDays?: Map<string, boolean>;
	liveDurationDays?: number;
	roleId?: string;
	side?: string;
}

interface IDateRangeResponse extends Partial<IMetadataForDateRangeResponse> {
	left?: number;
}

// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7104
// :facepalm:
// eslint-disable-next-line @rushstack/no-new-null
type FirebaseyOptional<T> = T | null | undefined;
interface ConstrantAssignmentFields {
	responsibleProjectMemberId?: FirebaseyOptional<string>;
	responsibleProjectMemberRoleId?: FirebaseyOptional<string>;
	assignedProjectMemberId?: FirebaseyOptional<string>;
	assignedProjectMemberRoleId?: FirebaseyOptional<string>;
	requestedByProjectMemberId?: FirebaseyOptional<string>;
	requestedByProjectMemberRoleId?: FirebaseyOptional<string>;
	// previousAssignedProjectMembers?: IPreviousAssignedProjectMember[] | undefined;
}

type NewConstrantAssignmentFields = Pick<
	TicketFields,
	'assignedProjectMemberId' | 'assignedProjectMemberRoleId'
>;
// END PLACEHOLDER REPLACE AS PART OF TP-7104

/**
 * TicketFieldsService is used to generate data to be changed when ticket fields on a groupedTicket wants
 * to be updated. Most public functions will return a Map<Ticket, object> that will have the object to be
 * written based on the ticket that has changed.

 * This service is intended to be common core of "applying complex changes to a Ticket (GroupedTicket technically)".
 */
export class TicketFieldsService {
	public calendarWorkdays!: CalendarWorkdays;

	// Necesary for guessXFromDate calculation
	private _startDate: string = '';
	private _startX!: number;
	private _columnWidth!: number;

	/**
	 * Init
	 * @param startDate generally this is activeLineDate
	 * @param startX generally this is activeLineX
	 * @param columnWidth generally this is calDayWidthPixels
	 */
	public setup(
		calendarWorkdays: CalendarWorkdays,
		startDate: string,
		startX: number,
		columnWidth: number = PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH
	): void {
		this.calendarWorkdays = calendarWorkdays;
		this._startDate = startDate;
		this._startX = startX;
		this._columnWidth = columnWidth;
	}

	// a special update that does not run through the "writeDataToGroupedTicket" flow
	// but instead updates each ticket more manually with per-ticket curated data
	// this currently works with multi because duration is not clearable
	public setDuration(
		groupedTicket: GroupedTicket,
		duration: number,
		ticketDataOverride?: Map<Ticket, IDurationUpdateData>
	): Map<Ticket, TicketFields> {
		const updateMap = new Map<Ticket, object>();
		groupedTicket.srcList.forEach((ticket) => {
			const override = ticketDataOverride?.get(ticket);
			const existingData: IDurationUpdateData = {
				liveStart: override?.liveStart || ticket.view.liveStart,
				liveExceptionDays: override?.liveExceptionDays || ticket.view.liveExceptionDays,
				roleId: override?.roleId || ticket.view.roleId,
				side: override?.side || ticket.view.side,
			};

			if (!existingData.liveStart || existingData.side === Side.PULL) {
				// Logging.warning('DEVTEXT - no date, what do');
				updateMap.set(ticket, { liveDurationDays: duration });
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} else if (duration !== null && (duration as any) !== '') {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const durationData = this.calendarWorkdays.getMetadataForDuration(
					existingData.liveExceptionDays,
					existingData.roleId,
					existingData.liveStart,
					duration
				);
				updateMap.set(ticket, durationData);
			}
		});

		return updateMap;
	}

	public setStartAndDuration(
		groupedTicket: GroupedTicket,
		start: string,
		duration: number
	): Map<Ticket, TicketFields> {
		const left = this.guessXFromDate(start, 1);
		const updateMap = new Map<Ticket, object>();

		groupedTicket.srcList.forEach((ticket) => {
			updateMap.set(ticket, { liveStart: start, left });
		});

		//since it's the same groupedTicket we can assume both update maps have the same keys
		//if this comes up again, break out a "merge update maps" function
		const durationOutputMap = this.setDuration(groupedTicket, duration, updateMap);
		durationOutputMap.forEach((value, key) => {
			const firstUpdate = updateMap.get(key);
			updateMap.set(key, { ...firstUpdate, ...value });
		});

		return updateMap;
	}

	/**
	 * @returns a map of updates to be applied, indexed by ticket and containing update objects
	 * May return empty objects if there are no calculable updates (ticket right of active line)
	 */
	public setDateRange(
		groupedTicket: GroupedTicket,
		dateRange: { liveStart: string; liveFinish: string }
	): Map<Ticket, IDateRangeResponse> {
		if (!dateRange) {
			return new Map();
		}
		const updateMap = new Map<Ticket, IDateRangeResponse>();

		groupedTicket.srcList.forEach((ticket) => {
			// skip if the update will make the ticket a pull ticket
			// or if the ticket being edited is "invalid" (missing required data)
			if (dateRange.liveStart > this._startDate) {
				updateMap.set(ticket, {});
				return;
			}
			// get left
			const dateRangeData = Object.assign(
				this.calendarWorkdays.getMetadataForDateRange(
					ticket.view.liveExceptionDays || new Map(),
					ticket.view.roleId,
					dateRange.liveStart,
					dateRange.liveFinish
				),
				{
					// left: this.planState.activeColumnService.guessXFromDate(dateRange.liveStart, 1)
					left: this.guessXFromDate(dateRange.liveStart, 1),
				}
			);

			updateMap.set(ticket, dateRangeData);
		});
		return updateMap;
	}

	public convertPlannedToActual(groupedTicket: GroupedTicket): Map<Ticket, object> {
		const updateMap = new Map<Ticket, object>();
		groupedTicket.srcList.forEach((ticket) => {
			updateMap.set(ticket, this.convertTicketPlannedToActual(ticket));
		});
		return updateMap;
	}

	public convertPlannedToStarted(groupedTicket: GroupedTicket): Map<Ticket, object> {
		const updateMap = new Map<Ticket, object>();
		groupedTicket.srcList.forEach((ticket) => {
			updateMap.set(ticket, ticket.convertPlannedToStarted());
		});
		return updateMap;
	}

	//ticket is delibrate, this will only work on an individual ticket, when we get to multiples
	//loop it, either here, or when calling this
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private convertTicketPlannedToActual(t: Ticket): any {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const newData: any = {};

		//this nonsense (kind of) happens in the old edit, and things break without it. Consider this a short term workaround...
		if (!t.view.liveExceptionDays) {
			t.view.liveExceptionDays = new Map();
		}
		if (!t.view.plannedExceptionDays) {
			t.view.plannedExceptionDays = new Map();
		}

		Object.keys(plannedOnly).forEach((plannedKey) => {
			const plannedKeyData = plannedOnly[plannedKey];

			//special handling, should eventually migrate to an optional "clone" function in the
			//attribute config. Only going to clone it on planned -> actual
			//TODO: 2021-12-19. Near as I'm able to tell, this check is broken in both old and new edit. Someone should put some time into debugging it...
			// see also the above "nonsense" comment
			if (plannedKey === 'plannedExceptionDays') {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const datas = !t.view[plannedKey] ? t.view[plannedKey] : new Map(t.view[plannedKey] as any);
				newData[plannedKeyData.lastPlannedKey] = datas;
				newData[plannedKeyData.actualKey] = datas;
			} else {
				newData[plannedKeyData.lastPlannedKey] = t.view[plannedKey];
				newData[plannedKeyData.actualKey] = t.view[plannedKey];
			}
		});
		if (t.view.liveLastPromise) {
			const newPin = clonePin(t.view.liveLastPromise);
			newPin.data.actualFinish = newData.actualFinish;
			newData.liveLastPromise = newPin;
		}
		// MOC-4354 - Special override to prevent constraint from displaying broken data when completed before accepted
		if (t.view.assignedProjectMemberId) {
			newData.responsibleProjectMemberId = t.view.assignedProjectMemberId;
			newData.responsibleProjectMemberRoleId = t.view.assignedProjectMemberRoleId;
			newData.assignedProjectMemberId = null;
			newData.assignedProjectMemberRoleId = null;
		}
		return newData;
	}

	public revertActualToPlanned(groupedTicket: GroupedTicket): Map<Ticket, object> {
		const updateMap = new Map<Ticket, object>();
		groupedTicket.srcList.forEach((ticket) => {
			updateMap.set(ticket, this.revertTicketActualToPlanned(ticket));
		});
		return updateMap;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private revertTicketActualToPlanned(t: Ticket): any {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const newData: any = {};

		Object.keys(plannedOnly).forEach((plannedKey) => {
			const plannedKeyData = plannedOnly[plannedKey];
			newData[plannedKey] = t.view[plannedKeyData.lastPlannedKey];
			newData[plannedKeyData.actualKey] = null;
			newData[plannedKeyData.lastPlannedKey] = null;
		});
		if (t.view.liveLastPromise) {
			const newPin = clonePin(t.view.liveLastPromise);
			delete newPin.data.actualFinish;
			// newPin.data.actualFinish = newData.actualFinish;
			newData.liveLastPromise = newPin;
		}

		return newData;
	}

	/**
	 * Setting the workdays.
	 * Feels a bit silly to do the grouping thing for this one as workdays will be ticket specific, but... consistency
	 */
	public setWorkdays(groupedTicket: GroupedTicket, workdays: boolean[]): Map<Ticket, object> {
		const updateMap = new Map<Ticket, object>();
		groupedTicket.srcList.forEach((ticket) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			updateMap.set(ticket, this.setTicketWorkdays(groupedTicket, workdays) as any);
		});
		return updateMap;
	}

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	private setTicketWorkdays(ticket: Ticket, workdays: boolean[]) {
		if (ticket.view.side === Side.PULL) {
			return;
		}

		if (!workdays) {
			logger.log('DEVTEXT - no days, what do');
			return;
		}
		//if the first one is off, just flip it back on
		// if (workdays.length && !workdays[0]) {
		// 	Logging.notice(
		// 		ticket.view.liveDurationDays === 1 ?
		// 		WARNING_SET_START_NONWORK_1DAY
		// 		:
		// 		WARNING_SET_START_NONWORK
		// 	);
		// }

		// const persistentData = this.persistentData$.getValue();
		const workdaysData = this.calendarWorkdays.getMetadataForWorkdays(
			workdays,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			ticket.view.liveExceptionDays as any,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			ticket.view.liveDurationDays as any,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			ticket.view.roleId as any,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			ticket.view.liveStart as any,
			false
		);
		return workdaysData;
	}

	/**
	 * Setting variance related data on the most recent promise pin.
	 * Mostly copied from the legacy [[TicketEditService]]
	 */
	public setCollectedVariance(
		groupedTicket: GroupedTicket,
		varianceId?: string,
		varianceComment?: string
	): Map<Ticket, object> {
		const updateMap = new Map<Ticket, object>();
		groupedTicket.srcList.forEach((ticket) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			updateMap.set(
				ticket,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				this.setCollectedTicketVariance(groupedTicket, varianceId, varianceComment) as any
			);
		});
		return updateMap;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private setCollectedTicketVariance(ticket: Ticket, varianceId?: string, varianceComment?: string): any {
		if (ticket.view.liveLastPromise) {
			const newPin = applyVarianceToPin(ticket.view.liveLastPromise, varianceId, varianceComment);
			return { liveLastPromise: newPin };
		}
	}

	/**
	 * The main way to write data back to the ticket from edit
	 * @param  data                     Object representing the data to write
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public writeDataToGroupedTicket(groupedTicket: GroupedTicket, data: any): Map<Ticket, object> | undefined {
		if (!data || typeof data !== 'object') {
			return;
		}

		// This check likely originated as a safeguard to avoid overwriting fields unintentionally,
		// particularly during multi-edit operations. It was probably a workaround for a bug in the
		// legacy edit flow (see writeCombinationBackToList in ticket-edit.service.ts). Although this function now handles both single and multi-edit cases,
		// we inherited this logic from the older implementation.
		//
		// Over time, we reduced the strictness of this check: it now only filters out `undefined` values,
		// since `null` and empty strings are considered valid inputs for some fields.
		function isEmpty(dat: null | undefined | string | object): boolean {
			return dat === undefined;
		}

		function isSame(a: string, b: string): boolean {
			return (isEmpty(a) && isEmpty(b)) || a === b;
		}

		function isSameInTicketList(key: string, list: Map<string, Ticket>): boolean {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			return Array.from(list.keys()).every((t) => isSame(t[key as any], data[key]));
		}

		if (groupedTicket.srcList.size > 0) {
			Object.keys(data).forEach((key) => {
				// only write the changed keys in multi-edit, this is to avoid overriting certain unmodified keys
				if (isSameInTicketList(key, groupedTicket.srcList)) {
					delete data[key];
				}
			});
		}
		return this.writeData(groupedTicket.srcList, data);
	}

	/**
	 * For persisting data back to the ticketList
	 * @param  list [description]
	 * @param  data [description]
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private writeData(list: ListLike<Ticket>, data: any): Map<Ticket, object> {
		const updateMap = new Map<Ticket, object>();
		list.forEach((t) => {
			const subData = data && data.size ? data.get(t.$id) : data;
			updateMap.set(t, subData);
		});
		return updateMap;
	}

	/**
	 * thin wrapper around the common version with some defaults
	 * Until we just fix this once and for all...
	 * - if dayOffset is 0 that means you'll get the x for the finishDate
	 * - if dayOffset is 1 that means you'll get the x for the startDate
	 * - and because this is a number, a 2 means the value will shift one more
	 * day to the left of the start date. And a -1 will shift one more day to the
	 * right of the finish data (this has no practical purpose)
	 */
	public guessXFromDate(
		date: string,
		dayOffset: number = 0,
		activeLineOverride?: { date: string; x: number }
	): number {
		const startDate = activeLineOverride ? activeLineOverride.date : this._startDate;
		const startX = activeLineOverride ? activeLineOverride.x : this._startX;

		return guessXFromDate(date, dayOffset, startDate, startX, this._columnWidth);
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7104

	// half converted from old edit, gave up
	// public setAssignedProjectMember(
	// 	combined: GroupedTicket,
	// 	newPm: ProjectMember,
	// 	// assignedProjectMemberId: string,
	// ): void {
	// 	const newData = {} as any;
	// 	const assignedProjectMemberId = newPm ? newPm.$id : null;
	// 	// const newPm = this.projectMembersSync.get(assignedProjectMemberId);
	// 	const newAssignedProjectMemberRoleId = newPm && newPm.view.roleId ? newPm.view.roleId : null;
	// 	newData.assignedProjectMemberId = assignedProjectMemberId || null;
	// 	newData.assignedProjectMemberRoleId = newAssignedProjectMemberRoleId;

	// 	// This sets a new requestedByProjectMember if the current assigned to re-assigns the constraint
	// 	if (
	// 		combined.view.liveAssignedProjectMember &&
	// 		// combined.ticketsInPlanRegion && //<--- special floating space behavior
	// 		combined.view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ACCEPTED
	// 	) {
	// 		// Uncomment this if we want to recalc primary constraint role during re-assignment
	// 		const oldPm = combined.view.liveAssignedProjectMember;
	// 		const oldAssignedProjectMemberRoleId: string = oldPm && oldPm.view.roleId ? oldPm.view.roleId : null;
	// 		const primaryRoleId = combined.data.assignedProjectMemberRoleId
	// 			? combined.data.assignedProjectMemberRoleId
	// 			: oldAssignedProjectMemberRoleId;
	// 		newData['requestedByProjectMemberId'] = combined.data.liveAssignedProjectMemberId;
	// 		newData['requestedByProjectMemberRoleId'] = primaryRoleId; //combined.data.assignedProjectMemberRoleId;
	// 	}

	// 	const newPreviousProjectMemberRoleId: string = newPm && newPm.view.roleId ? newPm.view.roleId : null;
	// 	const primaryRoleId = combined.data.assignedProjectMemberRoleId
	// 		? combined.data.assignedProjectMemberRoleId
	// 		: newPreviousProjectMemberRoleId;
	// 	const listOfAssignments: IPreviousAssignedProjectMember[] =
	// 		combined.data.previousAssignedProjectMembers || [];
	// 	listOfAssignments.push({
	// 		assignedProjectMemberId: assignedProjectMemberId || null,
	// 		requestedByProjectMemberId:
	// 			newData['requestedByProjectMemberId'] ||
	// 			combined.data.requestedByProjectMemberId ||
	// 			assignedProjectMemberId,
	// 		roleId: primaryRoleId,
	// 		reassignedAt: fbdbTime.syncTime,
	// 	});
	// 	newData['previousAssignedProjectMembers'] = listOfAssignments;

	// 	this.ticketEditService.writeCombinationBackToList(combined, newData);
	// }

	// ported from nest
	private resetConstraintAssignment(newData: ConstrantAssignmentFields): ConstrantAssignmentFields {
		return {
			assignedProjectMemberId: newData.assignedProjectMemberId,
			assignedProjectMemberRoleId: newData.assignedProjectMemberRoleId,
			responsibleProjectMemberId: null,
			responsibleProjectMemberRoleId: null,
		};
	}

	// ported from nest
	private skipConstraintReassignment(): ConstrantAssignmentFields {
		return {
			assignedProjectMemberId: undefined,
			assignedProjectMemberRoleId: undefined,
			responsibleProjectMemberId: undefined,
			responsibleProjectMemberRoleId: undefined,
		};
	}
	// ported from nest - basically untested
	/**
	 * Accepts a ticket and a new constraint assignment and calc's a combined suite of assignment data
	 * See https://mocasystems.atlassian.net/wiki/spaces/DI/pages/3028123654/Touchplan+format+export+import+constraint+assignments#Operation%3A
	 * for details (this function is structured to match the format of that doc)
	 * @param ticket Existing ticket (most likely in firebase, but potentially a "blank" new ticket)
	 * @param newAssignedProjectMemberIds the new data being added
	 * @returns the new data to be localUpdated into the ticket
	 */
	private updateTicketConstraintAssignment(
		ticket: Ticket,
		newAssignedProjectMemberIds: NewConstrantAssignmentFields
	): ConstrantAssignmentFields {
		// no assignment data, skip
		// this makes sense for undefined, null _may_ want to throw an error if that ever becomes possible
		if (!newAssignedProjectMemberIds.assignedProjectMemberId) {
			return this.skipConstraintReassignment();
		}
		// constraint is assigned, but not accepted
		if (!ticket.view.responsibleProjectMemberId) {
			// and hasn't changed
			if (ticket.view.assignedProjectMemberId === newAssignedProjectMemberIds.assignedProjectMemberId) {
				return this.skipConstraintReassignment();
			} else {
				//and has changed
				const assignments = this.resetConstraintAssignment(newAssignedProjectMemberIds);
				return assignments;
			}
		}

		// constraint has been accepted, but the assignment has changed
		if (ticket.view.responsibleProjectMemberId) {
			// and hasn't changed
			if (ticket.view.responsibleProjectMemberId === newAssignedProjectMemberIds.assignedProjectMemberId) {
				return this.skipConstraintReassignment();
			} else {
				//and has changed
				const assignments = this.resetConstraintAssignment(newAssignedProjectMemberIds);
				return assignments;
			}
		}

		// default case (do nothing)
		return {};
	}
	// very temp - probably shouldn't be using the group?
	public updateConstraintAssignment(
		group: GroupedTicket,
		newAssignedProjectMemberIds: NewConstrantAssignmentFields
	): Map<Ticket, object> {
		const data = this.updateTicketConstraintAssignment(group, newAssignedProjectMemberIds);
		return this.writeData(group.srcList, data);
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-7104
}
